import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { useReservableDate } from "~/services/reservation";

// "yyyyMMdd" → 로컬 타임존 Date
const parseYyyymmdd = (s: string) =>
  new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8))
  );

/**
 * 예약 캘린더 비활성화 날짜 훅.
 * reservable/date 응답의 calendar(false = 예약불가: 정기휴무 + 임시휴무)와
 * holidays(임시 휴무일 — 호텔은 체크인만 차단 정책이라 calendar 미반영)를
 * CalendarNew 의 disabledDates 로 넘길 수 있는 Date[] 로 변환한다.
 * 캘린더 월 이동 시 handleMonthNavigate(isNext)를 연결하면 해당 월 기준으로 재조회한다.
 */
export const useReservationDisabledDates = (
  companySeq: number,
  productType: string
) => {
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const yearMonth = format(viewMonth, "yyyy-MM");

  const { data } = useReservableDate(
    companySeq,
    yearMonth,
    !!companySeq,
    productType
  );

  // 예약 불가일(calendar === false). 호텔은 임시 휴무일이 여기 포함되지 않음.
  const disabledDates = useMemo(() => {
    const calendar = (data?.calendar ?? {}) as Record<
      string,
      boolean | null | undefined
    >;
    return Object.entries(calendar)
      .filter(([, reservable]) => reservable === false)
      .map(([dateStr]) => parseYyyymmdd(dateStr));
  }, [data]);

  // 임시 휴무일 (호텔: 체크인 차단 + 기간 포함 시 확인 모달용)
  const holidayDates = useMemo(
    () => (data?.holidays ?? []).map(parseYyyymmdd),
    [data]
  );

  const holidaySet = useMemo(() => new Set(data?.holidays ?? []), [data]);

  // 예약 마감시간 차단 날짜 (당일 + 마감 이후 익일). 비호텔은 calendar에도 반영되어 있음.
  const deadlineDates = useMemo(
    () => (data?.deadlineDates ?? []).map(parseYyyymmdd),
    [data]
  );

  const handleMonthNavigate = useCallback((isNext: boolean) => {
    setViewMonth(
      (prev) =>
        new Date(prev.getFullYear(), prev.getMonth() + (isNext ? 1 : -1), 1)
    );
  }, []);

  return {
    disabledDates,
    holidayDates,
    holidaySet,
    deadlineDates,
    handleMonthNavigate,
  };
};
